import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportTechnicians } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function TechniciansReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-technicians', qBuilt],
    queryFn: () => fetchReportTechnicians(qBuilt),
  });

  const rows = q.data ?? [];

  const chartData = useMemo(
    () =>
      [...rows]
        .slice(0, 12)
        .map((r) => ({ name: r.techName.slice(0, 18), completed: r.ticketsCompleted, revenue: Number(r.revenue) })),
    [rows],
  );

  const csv = () => {
    exportToCsv(
      rows.map((r) => ({
        techName: r.techName,
        ticketsAssigned: r.ticketsAssigned,
        ticketsCompleted: r.ticketsCompleted,
        avgResolutionHours: r.avgResolutionHours,
        totalLaborMinutes: r.totalLaborMinutes,
        billableLaborMinutes: r.billableLaborMinutes,
        revenue: r.revenue,
        topRepairs: r.topRepairs.map((t) => `${t.serviceName} (${t.count})`).join('; '),
      })),
      'technicians.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading technicians…</div>;
  if (q.isError) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load technician report.</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/reports/leaderboard"
          className="inline-flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/20"
        >
          <Trophy className="h-4 w-4" /> View gamified leaderboard
        </Link>
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Completed tickets</h3>
        <div className="mt-4 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number, name: string) => (name === 'revenue' ? formatCurrency(v) : v)} />
              <Bar dataKey="completed" fill="#6366f1" radius={[0, 4, 4, 0]} name="Completed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Technician</th>
              <th className="px-4 py-3">Assigned</th>
              <th className="px-4 py-3">Completed</th>
              <th className="px-4 py-3">Avg hrs</th>
              <th className="px-4 py-3">Labor (min)</th>
              <th className="px-4 py-3">Billable (min)</th>
              <th className="px-4 py-3">Revenue</th>
              <th className="px-4 py-3">Top repairs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.techId} className="border-b border-slate-100">
                <td className="px-4 py-2 font-medium">{r.techName}</td>
                <td className="px-4 py-2">{r.ticketsAssigned}</td>
                <td className="px-4 py-2">{r.ticketsCompleted}</td>
                <td className="px-4 py-2">{r.avgResolutionHours.toFixed(1)}</td>
                <td className="px-4 py-2">{r.totalLaborMinutes}</td>
                <td className="px-4 py-2">{r.billableLaborMinutes}</td>
                <td className="px-4 py-2">{formatCurrency(r.revenue)}</td>
                <td className="max-w-xs px-4 py-2 text-xs text-slate-600">
                  {r.topRepairs.map((t) => `${t.serviceName} ×${t.count}`).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length ? <p className="p-6 text-center text-slate-500">No technician activity in this period.</p> : null}
      </div>
    </div>
  );
}
